import React, { useCallback, useEffect, useState } from 'react';
import candidateWorkflowService, { CandidateWorkflow } from '../../../services/candidateWorkflow.service';
import {
  CandidateStage, CandidatePriority,
  CANDIDATE_STAGE_CONFIG, CANDIDATE_PRIORITY_CONFIG, stageInfo,
} from '../../../constants/candidateStage';
import { relativeZeit, istUeberfaellig } from './zeit';

/**
 * Kopfbereich der Kandidatenansicht: beantwortet auf einen Blick, ob der
 * Kandidat bearbeitet wird, von wem, was zuletzt geschah und was als Nächstes
 * ansteht. Die nächste Aufgabe kommt aus recruiting_tasks (Regel 30).
 */

interface Props {
  candidateId: string;
  benutzerNamen?: Record<string, string>;
  aktualisierungsZaehler?: number;
  onGeaendert?: () => void;
}

const AKTION_TEXT: Record<string, string> = {
  CALL_ATTEMPTED: 'Kontaktversuch',
  CALL_CONNECTED: 'Telefonat',
  NOTE_CREATED: 'Notiz',
  EMAIL_SENT: 'E-Mail gesendet',
  EMAIL_RECEIVED: 'E-Mail erhalten',
  CANDIDATE_STAGE_CHANGED: 'Status geändert',
  CANDIDATE_ASSIGNED: 'Zuständigkeit geändert',
  TASK_CREATED: 'Aufgabe angelegt',
  TASK_COMPLETED: 'Aufgabe erledigt',
  stage_changed: 'Status geändert',
};

const kachel: React.CSSProperties = { minWidth: 0 };
const kachelTitel: React.CSSProperties = {
  fontSize: '0.72rem', color: 'var(--text-tertiary)', marginBottom: '3px',
  textTransform: 'uppercase', letterSpacing: '0.04em',
};

export const CandidateWorkflowHeader: React.FC<Props> = ({
  candidateId, benutzerNamen = {}, aktualisierungsZaehler = 0, onGeaendert,
}) => {
  const [daten, setDaten] = useState<CandidateWorkflow | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [aendert, setAendert] = useState(false);

  const laden = useCallback(async () => {
    try {
      setLaedt(true);
      setDaten(await candidateWorkflowService.workflow(candidateId));
      setFehler(null);
    } catch (e: any) {
      setFehler(e?.message || 'Der Arbeitsstand konnte nicht geladen werden.');
    } finally {
      setLaedt(false);
    }
  }, [candidateId]);

  useEffect(() => { laden(); }, [laden, aktualisierungsZaehler]);

  const stufeAendern = async (stage: CandidateStage) => {
    setFehler(null);
    try {
      setAendert(true);
      await candidateWorkflowService.changeStage(candidateId, stage);
      await laden();
      onGeaendert?.();
    } catch (e: any) {
      setFehler(e?.message || 'Der Status konnte nicht geändert werden.');
    } finally {
      setAendert(false);
    }
  };

  const prioAendern = async (priority: CandidatePriority) => {
    try {
      setAendert(true);
      await candidateWorkflowService.setzePrioritaet(candidateId, priority);
      await laden();
      onGeaendert?.();
    } catch (e: any) {
      setFehler(e?.message || 'Die Priorität konnte nicht geändert werden.');
    } finally {
      setAendert(false);
    }
  };

  if (laedt && !daten) {
    return <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Arbeitsstand wird geladen…</div>;
  }
  if (!daten) {
    return <div style={{ fontSize: '0.85rem', color: '#EF4444' }}>{fehler || 'Kein Arbeitsstand verfügbar.'}</div>;
  }

  const info = stageInfo(daten.stage);
  const letzte = daten.letzteAktivitaet;
  const naechste = daten.naechsteAufgabe;
  const ueberfaellig = naechste ? istUeberfaellig(naechste.due_at) : false;

  return (
    <div
      style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(158px, 1fr))', gap: '14px',
        padding: '12px 14px', borderRadius: 'var(--radius-md)',
        background: 'var(--glass)', border: '1px solid var(--glass-border)',
      }}
    >
      <div style={kachel}>
        <div style={kachelTitel}>Status</div>
        <select
          value={daten.stage}
          disabled={aendert}
          onChange={(e) => stufeAendern(e.target.value as CandidateStage)}
          style={{
            width: '100%', padding: '4px 8px', fontSize: '0.85rem', fontWeight: 600,
            borderRadius: 'var(--radius-md)', cursor: aendert ? 'wait' : 'pointer',
            color: info.color, background: info.bgColor, border: `1px solid ${info.color}40`,
          }}
        >
          <option value={daten.stage}>{CANDIDATE_STAGE_CONFIG[daten.stage].label}</option>
          {daten.allowedTargets.map((z) => (
            <option key={z} value={z}>{CANDIDATE_STAGE_CONFIG[z].label}</option>
          ))}
        </select>
      </div>

      <div style={kachel}>
        <div style={kachelTitel}>Priorität</div>
        <select
          value={daten.priority}
          disabled={aendert}
          onChange={(e) => prioAendern(e.target.value as CandidatePriority)}
          style={{
            width: '100%', padding: '4px 8px', fontSize: '0.85rem', fontWeight: 600,
            borderRadius: 'var(--radius-md)', border: '1px solid var(--glass-border)',
            background: 'var(--glass)', color: CANDIDATE_PRIORITY_CONFIG[daten.priority].color,
          }}
        >
          {(Object.keys(CANDIDATE_PRIORITY_CONFIG) as CandidatePriority[]).map((p) => (
            <option key={p} value={p}>{CANDIDATE_PRIORITY_CONFIG[p].label}</option>
          ))}
        </select>
      </div>

      <div style={kachel}>
        <div style={kachelTitel}>Zuständig</div>
        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', paddingTop: '4px' }}>
          {daten.ownerUserId
            ? (benutzerNamen[daten.ownerUserId] || 'Zugewiesen')
            : <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>Niemand</span>}
        </div>
      </div>

      <div style={kachel}>
        <div style={kachelTitel}>Letzte Aktivität</div>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)', paddingTop: '4px' }}>
          {letzte ? (
            <>
              <span style={{ fontWeight: 600 }}>{AKTION_TEXT[letzte.action] || letzte.action}</span>
              <span style={{ color: 'var(--text-tertiary)' }}>
                {' · '}{relativeZeit(letzte.occurred_at)}
                {letzte.user_name ? ` · ${letzte.user_name}` : ''}
              </span>
            </>
          ) : (
            <span style={{ color: 'var(--text-tertiary)' }}>Noch keine Aktivität</span>
          )}
        </div>
      </div>

      <div style={kachel}>
        <div style={kachelTitel}>Nächste Aufgabe</div>
        <div style={{ fontSize: '0.85rem', paddingTop: '4px', color: ueberfaellig ? '#EF4444' : 'var(--text-primary)' }}>
          {naechste ? (
            <>
              <span style={{ fontWeight: 600 }}>{naechste.title}</span>
              <span style={{ color: ueberfaellig ? '#EF4444' : 'var(--text-tertiary)' }}>
                {naechste.due_at ? ` · ${ueberfaellig ? 'überfällig seit ' : ''}${relativeZeit(naechste.due_at)}` : ' · ohne Frist'}
              </span>
            </>
          ) : (
            <span style={{ color: 'var(--text-tertiary)' }}>Keine offene Aufgabe</span>
          )}
        </div>
      </div>

      {fehler && (
        <div style={{ gridColumn: '1 / -1', color: '#EF4444', fontSize: '0.8rem' }}>{fehler}</div>
      )}
    </div>
  );
};

export default CandidateWorkflowHeader;
